import { userSelector } from 'common/globalComponents/selectors';
import Breadcrumbs from 'components/Common/Breadcrumb';
import Loader from 'components/Loader';
import AssetImageUploader from 'containers/blog/BlogList/components/AssetImageUploader';
import { useFormik } from 'formik';
import useRequest from 'hooks/useRequest';
import isEmpty from 'lodash/isEmpty';
import React, { useEffect, useState } from 'react';
import 'react-datepicker/dist/react-datepicker.css';
import { useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import Select from 'react-select';
import { toast } from 'react-toastify';
import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Form,
  FormFeedback,
  FormGroup,
  Input,
  InputGroup,
  InputGroupText,
  Label,
  Row,
} from 'reactstrap';
import { createStructuredSelector } from 'reselect';
import { MEAL_TIME, MODULE_TYPE } from 'utils/constants';
import { titleCaseIfExists } from 'utils/helper';
import { SMKLinks } from 'utils/links';
import * as Yup from 'yup';
import { FOOD_ITEM_UNITS } from '../constants';
import MultipleItems from './components/multipleItems';

const stateSelector = createStructuredSelector({
  user: userSelector,
});

const AddUpdateMealPlan = () => {
  const history = useHistory();
  const { id: mealPlanId } = useParams();

  toast.configure();

  const { user } = useSelector(stateSelector);

  const [mealPlanDetails, setAchievementDetails] = useState();

  const MIN_AGE_GROUP_VALUES = [
    {
      label: '6 Months',
      value: 6,
    },
    {
      label: '9 Months',
      value: 9,
    },
    {
      label: '12 Months',
      value: 12,
    },
    {
      label: '18 Months',
      value: 18,
    },
    {
      label: '24 Months',
      value: 24,
    },
    {
      label: '3 Years',
      value: 36,
    },
    {
      label: '4 Years',
      value: 48,
    },
  ];

  const MAX_AGE_GROUP_VALUES = [
    {
      label: '9 Months',
      value: 9,
    },
    {
      label: '12 Months',
      value: 12,
    },
    {
      label: '18 Months',
      value: 18,
    },
    {
      label: '24 Months',
      value: 24,
    },
    {
      label: '3 Years',
      value: 36,
    },
    {
      label: '4 Years',
      value: 48,
    },
    {
      label: '18 Years',
      value: 216,
    },
  ];

  const MEAL_TIME_VALUES = Object.keys(MEAL_TIME)?.map((e) => ({
    label: titleCaseIfExists(e),
    value: MEAL_TIME[e],
  }));

  const AMOUNT_UNITS = [...new Set(Object.values(FOOD_ITEM_UNITS)?.flat(2) ?? [])]?.flat(2)?.map((e) => ({
    label: e?.toUpperCase(),
    value: e,
  }));

  const [getAchievementRequest, { loading: getAchievementLoading }] = useRequest({
    method: 'GET',
    url: `/nutrition/diet-plan/admin/${mealPlanId}`,
    onSuccess: (data) => {
      setAchievementDetails(data);
    },
    onError: (err) => {
      toast.error(err?.message?.message || err?.message);
    },
  });

  const [addAchievementRequest, { loading: addAchievementLoading }] = useRequest({
    method: 'POST',
    url: '/nutrition/diet-plan/admin',
    onSuccess: (data) => {
      const url = SMKLinks.MEAL_PLAN_LIST;
      history.replace(url);
      toast.success('Success! Meal Plan has been added.');
    },
    onError: (err) => {
      toast.error(err?.message?.message || err?.message);
    },
  });

  const [updateAchievementRequest, { loading: updateAchievementLoading }] = useRequest({
    method: 'PUT',
    url: `/nutrition/diet-plan/admin/${mealPlanId}`,
    onSuccess: () => {
      toast.success('Success! Meal Plan has been updated.');
      window.location.reload();
    },
    onError: (err) => {
      toast.error(err?.message?.message || err?.message);
    },
  });

  const editInitialValues = () => {
    return {
      active: mealPlanDetails?.active,
      deleted: mealPlanDetails?.deleted,
      name: mealPlanDetails?.name,
      ageGroupMin: mealPlanDetails?.ageGroupMin || 0,
      ageGroupMax: mealPlanDetails?.ageGroupMax || 0,
      mealTime: mealPlanDetails?.mealTime,
      image: mealPlanDetails?.image,

      items: !isEmpty(mealPlanDetails?.items)
        ? mealPlanDetails?.items?.map((e) => ({
            amount: e?.amount,
            amountUnit: e?.amountUnit,
            calories: e?.calories,
            ...(!isEmpty(e?.recipe) ? { recipeId: e?.recipe?.id } : {}),
            ...(!isEmpty(e?.foodItem) ? { foodItemId: e?.foodItem?.id } : {}),
            isFoodItem: !isEmpty(e?.foodItem),
          }))
        : null,
      // portions: mealPlanDetails?.portions || null,
    };
  };

  const getInitialValues = () => ({
    active: true,
    deleted: false,
    name: '',
    ageGroupMin: 0,
    ageGroupMax: 0,
    mealTime: '',
    image: null,

    items: null,
    // portions: null,
  });

  const formik = useFormik({
    enableReinitialize: true,
    validateOnChange: true,
    validateOnBlur: false,
    initialValues: isEmpty(mealPlanId) ? { ...getInitialValues() } : { ...editInitialValues() },
    onSubmit: async (values) => {
      const data = {
        active: values?.active,
        deleted: values?.deleted,
        name: values?.name,
        mealTime: values?.mealTime,
        ageGroupMin: values?.ageGroupMin,
        ageGroupMax: values?.ageGroupMax,
        imageId: values?.image?.id || null,
        items: !isEmpty(values?.items)
          ? values?.items?.map((e) => ({
              amount: e?.amount,
              amountUnit: e?.amountUnit,
              calories: e?.calories,
              ...(!isEmpty(e?.recipe) ? { recipeId: e?.recipe?.id } : {}),
              ...(!isEmpty(e?.foodItem) ? { foodItemId: e?.foodItem?.id } : {}),
            }))
          : null,
        // portions: values?.portions || null,
      };

      const payload = {
        body: data,
      };

      if (isEmpty(mealPlanId)) {
        addAchievementRequest(payload);
      } else {
        updateAchievementRequest(payload);
      }
    },
    validationSchema: Yup.object({
      name: Yup.string().required('Please provide name'),
      mealTime: Yup.string().required('Please provide meal time'),
    }),
  });

  useEffect(() => {
    if (!isEmpty(mealPlanId)) {
      getAchievementRequest();
    }
  }, [mealPlanId]);

  return (
    <div className="page-content">
      <Loader isActive={addAchievementLoading || getAchievementLoading || updateAchievementLoading} />
      <Container fluid>
        <Breadcrumbs title="Meal Plan" breadcrumbItem={`${isEmpty(mealPlanId) ? 'Add' : 'Edit'}  Meal Plan`} />
        <Form onSubmit={formik.handleSubmit}>
          <Row>
            <Col md={9}>
              <Card className="mb-4">
                <CardBody>
                  <Row className="align-items-center">
                    <Col md={12}>
                      <FormGroup className="mb-4">
                        <Label for="name" className="form-label  ">
                          Name <span className="text-danger">*</span>
                        </Label>
                        <Input
                          id="name"
                          name="name"
                          type="text"
                          className="form-control"
                          placeholder="Enter Name..."
                          invalid={!!(formik.touched.name && formik.errors.name)}
                          {...formik.getFieldProps('name')}
                        />
                        <FormFeedback className="d-block">{formik.errors.name}</FormFeedback>
                      </FormGroup>
                    </Col>

                    <Col md={12}>
                      <FormGroup className="mb-4">
                        <Label for="mealTime" className="form-label">
                          Meal Time <span className="text-danger">*</span>
                        </Label>
                        <Select
                          id="mealTime"
                          options={MEAL_TIME_VALUES}
                          value={
                            MEAL_TIME_VALUES?.find(
                              (e) => e?.value?.toLowerCase() === formik.values.mealTime?.toLowerCase()
                            ) || null
                          }
                          onChange={(e) => formik.setFieldValue('mealTime', e?.value)}
                        />
                        <FormFeedback className="d-block">{formik.errors.mealTime}</FormFeedback>
                      </FormGroup>
                    </Col>

                    <Col md={6}>
                      <FormGroup className="mb-4">
                        <Label for="ageGroupMin">Minimum Age Group</Label>
                        <Select
                          id="ageGroupMin"
                          options={MIN_AGE_GROUP_VALUES}
                          value={MIN_AGE_GROUP_VALUES?.find((e) => e?.value == formik.values.ageGroupMin) || null}
                          onChange={(e) => formik.setFieldValue('ageGroupMin', e?.value)}
                        />

                        <FormFeedback className="d-block">{formik.errors.ageGroupMin}</FormFeedback>
                      </FormGroup>
                    </Col>

                    <Col md={6}>
                      <FormGroup className="mb-4">
                        <Label for="ageGroupMax">Maximum Age Group</Label>
                        <Select
                          id="ageGroupMax"
                          options={MAX_AGE_GROUP_VALUES}
                          value={MAX_AGE_GROUP_VALUES?.find((e) => e?.value == formik.values.ageGroupMax) || null}
                          onChange={(e) => formik.setFieldValue('ageGroupMax', e?.value)}
                        />
                        <FormFeedback className="d-block">{formik.errors.ageGroupMax}</FormFeedback>
                      </FormGroup>
                    </Col>
                  </Row>
                </CardBody>
              </Card>

              <MultipleItems
                unitsList={AMOUNT_UNITS}
                formik={formik}
                keyName={'items'}
                mealPlanDetails={mealPlanDetails}
              />
            </Col>
            <Col md={3}>
              <Card>
                <CardBody>
                  <div>
                    {user?.entityPermissions?.nutrition_meal_plan?.publish && mealPlanId && (
                      <Button
                        type="button"
                        color={mealPlanDetails?.active ? 'danger' : 'success'}
                        className="mb-3 w-100"
                        onClick={() => {
                          const action = mealPlanDetails?.active ? 'unpublish' : 'publish';
                          const isConfirm = confirm(`Are you sure you want to ${action} this item?`);

                          if (isConfirm) {
                            formik.setFieldValue('active', !mealPlanDetails?.active);
                            formik.setFieldValue('deleted', false);
                            formik.handleSubmit();
                          }
                        }}>
                        {mealPlanDetails?.active ? 'Unpublish' : 'Publish'}
                      </Button>
                    )}

                    <Button type="submit" color="primary" className="mb-3 w-100">
                      {mealPlanId ? 'Save' : 'Add'}
                    </Button>

                    <Button
                      className="w-100"
                      color="light"
                      onClick={() => {
                        history.replace(SMKLinks.MEAL_PLAN_LIST);
                      }}>
                      Cancel
                    </Button>
                  </div>
                </CardBody>
              </Card>
              {/* <Card>
                <CardBody>
                  <Row className="align-items-center">
                    <Col md={12}>
                      <CardTitle className="mb-3">Active </CardTitle>
                      {[true, false].map((option, i) => (
                        <div key={`active_${i}`} className="form-check mb-3 d-inline-block me-4">
                          <input
                            type="radio"
                            id={`active_${option}`}
                            name="active"
                            className="form-check-input"
                            checked={formik.values.active === option}
                            onChange={() => formik.setFieldValue('active', option)}
                          />
                          <label className="form-check-label" htmlFor={`active_${option}`}>
                            {option ? 'Yes' : 'No'}
                          </label>
                        </div>
                      ))}
                    </Col>

                    <Col md={12}>
                      <CardTitle className="mb-3">Delete </CardTitle>
                      {[true, false].map((option, i) => (
                        <div key={`deleted_${i}`} className="form-check mb-3 d-inline-block me-4">
                          <input
                            type="radio"
                            id={`deleted_${option}`}
                            name="deleted"
                            className="form-check-input"
                            checked={formik.values.deleted === option}
                            onChange={() => formik.setFieldValue('deleted', option)}
                          />
                          <label className="form-check-label" htmlFor={`deleted_${option}`}>
                            {option ? 'Yes' : 'No'}
                          </label>
                        </div>
                      ))}
                    </Col>
                  </Row>
                </CardBody>
              </Card> */}

              <AssetImageUploader
                forType={MODULE_TYPE.RECIPE}
                formik={formik}
                fieldName={'image'}
                label={'Select Image'}
              />
            </Col>
          </Row>
        </Form>
      </Container>
    </div>
  );
};

export default AddUpdateMealPlan;
